/**
 * Rutas que no alcanzan todos los usuarios del tenant (OT-0017): proyectos,
 * consumo de IA, agentes conectados y la consola de la plataforma.
 *
 * Mismo mecanismo que `tenantInternalRoutes.tsx`: cada una pasa por
 * `TierGate` con un predicado de `lib/tiers.ts`, y la vista solo se monta —y
 * solo pide sus datos— cuando el permiso está resuelto a favor. El router
 * monta estos componentes, nunca las vistas directamente.
 *
 * No sustituye al backend: es lo que se muestra, no lo que se autoriza.
 */

import type { ReactNode } from 'react'
import { Navigate, useMatches } from '@tanstack/react-router'

import { TierGate } from '../auth/TierGate'
import { PlatformAccessGate } from '../components/platform/PlatformAccessError'
import {
  canAccessProjects,
  canUseAgentAccess,
  canViewAiUsage,
  isPlatformAdmin,
} from '../lib/tiers'
import { PlatformTenantDetailView } from './platform/PlatformTenantDetailView'
import { PlatformTenantsView } from './platform/PlatformTenantsView'
import { PlatformUsageView } from './platform/PlatformUsageView'
import { ProjectShell } from './projects/ProjectShell'
import { ProjectsListView } from './projects/ProjectsListView'
import { ConnectedAgentsView } from './settings/ConnectedAgentsView'
import { TenantUsageView } from './settings/TenantUsageView'

export function ProjectsListRoute() {
  return (
    <TierGate allow={(me) => canAccessProjects(me.role)}>
      <ProjectsListView />
    </TierGate>
  )
}

/**
 * `/projects/$projectId/*` — el gate va antes de `ProjectShell`, así que quien
 * no puede entrar a proyectos nunca dispara `GET /projects/{id}`.
 *
 * A estas URLs no solo se llega navegando: las arman la campana
 * (`NotificationsPanel.resolveSubjectUrl`) y los correos del backend
 * (`EmailNotificationService._artifact_url` / `_project_url`), sin rama por
 * tier. Un aprobador `external` que recibe "es tu turno de aprobar" llega
 * aquí; ver `ProjectRouteDenied`.
 */
export function ProjectShellRoute() {
  return (
    <TierGate allow={(me) => canAccessProjects(me.role)} denied={<ProjectRouteDenied />}>
      <ProjectShell />
    </TierGate>
  )
}

/** Rutas hijas de `/projects/$projectId` que nombran un artefacto. */
const ARTIFACT_ROUTE_IDS = new Set<string>([
  '/projects/$projectId/official/$artifactId',
  '/projects/$projectId/official/$artifactId/edit',
  '/projects/$projectId/official/$artifactId/preview',
])

/**
 * Sin acceso al proyecto. Si la URL nombra un artefacto, el destino es su
 * pantalla de aprobación (`/approvals/$artifactId`, OT-0013), que no pasa por
 * el proyecto; si no, la home, que para el `external` es su bandeja. A esas
 * URLs sin artefacto llega por el aviso y el correo de "te añadieron al
 * proyecto": un "sin acceso" ahí lo dejaría en un callejón sin salida.
 *
 * El `artifactId` se lee del match de la ruta hija, no de los params de esta
 * ruta: `/projects/$projectId` no declara `artifactId`.
 */
function ProjectRouteDenied() {
  const artifactId = useMatches({
    select: (matches) => {
      const match = matches.find((m) => ARTIFACT_ROUTE_IDS.has(m.routeId))
      const params: Partial<Record<string, string>> | undefined = match?.params
      return params?.artifactId
    },
  })

  if (artifactId) {
    return <Navigate to="/approvals/$artifactId" params={{ artifactId }} replace />
  }
  return <Navigate to="/" replace />
}

export function TenantUsageRoute() {
  return (
    <TierGate allow={(me) => canViewAiUsage(me.role)}>
      <TenantUsageView />
    </TierGate>
  )
}

export function ConnectedAgentsRoute() {
  return (
    <TierGate allow={(me) => canUseAgentAccess(me.role)}>
      <ConnectedAgentsView />
    </TierGate>
  )
}

/**
 * Pantallas de `/platform/*`: platform admin, y además `PlatformAccessGate`
 * muestra el aviso si el backend niega por MFA o por no ser del tenant
 * operador (OT-0073 §4.1).
 */
function PlatformRoute({ children }: { children: ReactNode }) {
  return (
    <TierGate allow={isPlatformAdmin}>
      <PlatformAccessGate>{children}</PlatformAccessGate>
    </TierGate>
  )
}

export function PlatformUsageRoute() {
  return (
    <PlatformRoute>
      <PlatformUsageView />
    </PlatformRoute>
  )
}

export function PlatformTenantsRoute() {
  return (
    <PlatformRoute>
      <PlatformTenantsView />
    </PlatformRoute>
  )
}

export function PlatformTenantDetailRoute() {
  return (
    <PlatformRoute>
      <PlatformTenantDetailView />
    </PlatformRoute>
  )
}
